import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowUpRight, Loader2, CheckCircle2, Instagram, Facebook, Linkedin, CalendarCheck, Star } from "lucide-react";
import { Reveal } from "./Reveal";
import { Logo } from "./Logo";
import { Link } from "react-router-dom";
import { scrollToId } from "../lib/nav";
import { useServices } from "../hooks/useContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_REVIEW_URL = "https://www.google.com/maps/search/?api=1&query=Rosevale+Accountants+Ltd+The+Innovation+Centre+Bristol+%26+Bath+Science+Park+Emersons+Green+Bristol+BS16+7FR";

export const CtaFooter = () => {
  const SERVICES = useServices();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/leads`, form);
      setStatus("sent");
      toast.success("Thanks! We'll be in touch within one working day.");
    } catch {
      setStatus("idle");
      toast.error("Something went wrong — please call us on 0117 321 0360.");
    }
  };

  const field =
    "w-full rounded-xl bg-white/10 border border-white/20 px-5 py-4 text-white placeholder-white/50 text-sm focus:border-[#40C0E0] transition-colors duration-300";

  return (
    <>
      <section id="contact" data-testid="contact-section" className="py-24 sm:py-36 bg-[#103080] relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#0090D0]/20 blur-3xl" />
        <div className="absolute -bottom-40 -left-24 w-[28rem] h-[28rem] rounded-full bg-[#40C0E0]/15 blur-3xl" />

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#40C0E0] font-medium mb-6">
              Free discovery call
            </p>
            <h2 className="font-serif font-semibold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-tight mb-8">
              Let's talk about your numbers.
            </h2>
            <p className="text-base sm:text-lg text-white/75 leading-relaxed mb-10 max-w-lg">
              An informal chat — in person at the Science Park or by video — to get to
              know you and the help you're looking for. No strings. No hidden fees.
            </p>
            <div className="space-y-4">
              <a
                href="tel:01173210360"
                data-testid="contact-phone-link"
                className="group flex items-center gap-4 text-white hover:text-[#40C0E0] transition-colors duration-300"
              >
                <span className="w-11 h-11 rounded-full bg-white/10 border border-white/15 flex items-center justify-center">
                  <Phone size={18} />
                </span>
                <span className="font-medium">0117 321 0360</span>
              </a>
              <a
                href="mailto:info@rosevaleaccountants.co.uk"
                data-testid="contact-email-link"
                className="group flex items-center gap-4 text-white hover:text-[#40C0E0] transition-colors duration-300"
              >
                <span className="w-11 h-11 rounded-full bg-white/10 border border-white/15 flex items-center justify-center">
                  <Mail size={18} />
                </span>
                <span className="font-medium">info@rosevaleaccountants.co.uk</span>
              </a>
              <div className="flex items-center gap-4 text-white/80" data-testid="contact-address">
                <span className="w-11 h-11 rounded-full bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
                  <MapPin size={18} />
                </span>
                <span className="text-sm leading-relaxed">
                  The Innovation Centre, Bristol &amp; Bath Science Park,<br />
                  Emersons Green, Bristol BS16 7FR · Mon–Fri, 9am–5pm
                </span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <motion.div
              className="rounded-2xl bg-white/5 backdrop-blur-xl border border-white/15 p-8 sm:p-10"
              data-testid="discovery-call-form-card"
            >
              {status === "sent" ? (
                <div className="text-center py-14" data-testid="form-success-message">
                  <CheckCircle2 size={52} className="text-[#40C0E0] mx-auto mb-5" strokeWidth={1.5} />
                  <h3 className="font-serif text-white text-3xl mb-3">Lovely — talk soon.</h3>
                  <p className="text-white/70 text-sm leading-relaxed max-w-xs mx-auto">
                    Thanks {form.name.split(" ")[0] || "there"} — we've got your details and
                    one of us will be in touch within one working day.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="discovery-call-form">
                  <input
                    required
                    data-testid="form-name-input"
                    className={field}
                    placeholder="Your name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                  <input
                    required
                    type="email"
                    data-testid="form-email-input"
                    className={field}
                    placeholder="Email address"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                  <input
                    data-testid="form-phone-input"
                    className={field}
                    placeholder="Phone (optional)"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                  <textarea
                    rows={3}
                    data-testid="form-message-input"
                    className={`${field} resize-none`}
                    placeholder="Tell us a little about your business..."
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                  />
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid="form-submit-button"
                    className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#40C0E0] text-[#103080] font-semibold px-8 py-4 text-sm sm:text-base hover:bg-white transition-colors duration-300 disabled:opacity-60"
                  >
                    {status === "sending" ? (
                      <>
                        <Loader2 size={18} className="animate-spin" /> Sending...
                      </>
                    ) : (
                      <>
                        Send us a message
                        <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                      </>
                    )}
                  </button>
                  <a
                    href="https://calendly.com/abbie-browne"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="form-calendly-link"
                    className="group w-full inline-flex items-center justify-center gap-2 rounded-full border border-white/30 bg-white/5 text-white font-semibold px-8 py-4 text-sm sm:text-base hover:bg-white hover:text-[#103080] transition-colors duration-300"
                  >
                    <CalendarCheck size={18} />
                    Prefer to pick a time? Book a call instantly
                    <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </a>
                  <p className="text-white/50 text-xs text-center pt-1">
                    No strings. No hidden fees. We reply within one working day.
                  </p>
                </form>
              )}
            </motion.div>
          </Reveal>
        </div>
      </section>

      <footer data-testid="site-footer" className="bg-[#0A1F55] py-14">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 pb-10 border-b border-white/10">
            <Logo light />
            <nav className="flex flex-wrap gap-x-8 gap-y-3" aria-label="Footer">
              {[
                { label: "Services", href: "#services" },
                { label: "Us", href: "#us" },
                { label: "Where", href: "#location" },
                { label: "Reviews", href: "#reviews" },
                { label: "Toolkit", href: "#toolkit" },
                { label: "Making Tax Digital", to: "/making-tax-digital" },
                { label: "Blog", to: "/blog" },
                { label: "Book a call", href: "#contact" },
              ].map((l) => l.to ? (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="text-sm text-white/60 hover:text-[#40C0E0] transition-colors duration-300"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={(e) => { e.preventDefault(); scrollToId(l.href); }}
                  className="text-sm text-white/60 hover:text-[#40C0E0] transition-colors duration-300"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="https://rosevaleaccountantsltd.portal.engager.app"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-portal-link"
                className="text-sm text-white/60 hover:text-[#40C0E0] transition-colors duration-300 inline-flex items-center gap-1"
              >
                Client Portal <ArrowUpRight size={13} />
              </a>
            </nav>
          </div>
          <div className="pt-8 pb-8 border-b border-white/10 flex flex-col lg:flex-row lg:items-baseline gap-3 lg:gap-10" data-testid="footer-services-row">
            <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 font-medium shrink-0">Our services</p>
            <nav className="flex flex-wrap gap-x-7 gap-y-2.5" aria-label="Services">
              {SERVICES.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`footer-service-${s.slug}`}
                  className="text-sm text-white/60 hover:text-[#40C0E0] transition-colors duration-300"
                >
                  {s.title}
                </Link>
              ))}
            </nav>
          </div>
          <div
            data-testid="footer-regulatory"
            className="pt-8 flex flex-col items-start md:flex-row md:items-center gap-5 md:gap-8"
          >
            <img
              src="/aat-licensed-white.png"
              alt="AAT Licensed Accountant"
              data-testid="footer-aat-logo"
              className="h-9 sm:h-10 w-auto shrink-0"
            />
            <div className="text-xs text-white/55 leading-relaxed">
              <p data-testid="footer-aat-licence">
                Abbie Browne is licensed and regulated by AAT under licence number 10253502.
              </p>
              <p className="mt-1">
                <span data-testid="footer-company-no">Company No: 11703834</span>
                <span className="mx-2 text-white/25">·</span>
                <span data-testid="footer-vat-no">VAT No: 404824709</span>
              </p>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6" data-testid="footer-actions-row">
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-google-review-link"
              className="group inline-flex items-center gap-2 rounded-full border border-[#40C0E0]/50 bg-[#40C0E0]/10 px-4 py-2 text-xs font-medium text-white hover:bg-[#40C0E0] hover:text-[#103080] transition-colors duration-300"
            >
              <Star size={14} className="fill-amber-400 text-amber-400 group-hover:fill-[#103080] group-hover:text-[#103080] transition-colors duration-300" />
              Leave us a Google review
              <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <div className="flex items-center gap-2" data-testid="footer-socials" aria-label="Follow Rosevale Accountants">
              <span className="text-xs text-white/45 mr-1">Follow us</span>
              {[
                { href: "https://www.instagram.com/rosevaleaccountants", label: "Instagram", Icon: Instagram, id: "footer-instagram-link" },
                { href: "https://www.facebook.com/rosevaleaccountants", label: "Facebook", Icon: Facebook, id: "footer-facebook-link" },
                { href: "https://www.linkedin.com/company/rosevale-accountants-ltd", label: "LinkedIn", Icon: Linkedin, id: "footer-linkedin-link" },
              ].map(({ href, label, Icon, id }) => (
                <a
                  key={id}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={id}
                  aria-label={`Rosevale Accountants on ${label}`}
                  title={label}
                  className="w-9 h-9 rounded-full border border-white/15 bg-white/5 flex items-center justify-center text-white/70 hover:bg-[#40C0E0] hover:border-[#40C0E0] hover:text-[#103080] transition-colors duration-300"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
        </div>
        <div className="mt-8 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-start md:items-center gap-3 text-xs text-white/45" data-testid="footer-legal-row">
            <p data-testid="footer-copyright">
              © 2026 Rosevale Accountants Ltd. All rights reserved.
              <span className="mx-2 text-white/25">·</span>
              <span data-testid="footer-built-by">Website built by Rosevale Accountants Ltd</span>
            </p>
            <p data-testid="footer-address-line">The Innovation Centre, Bristol &amp; Bath Science Park, Emersons Green, Bristol BS16 7FR</p>
          </div>
        </div>
      </footer>
    </>
  );
};
